import { DxOdataStoreService } from '@shared/components/dx-data-grid/data-access';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { computed, inject, Signal } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  EMPTY,
  Observable,
  catchError,
  finalize,
  forkJoin,
  from,
  map,
  of,
  pipe,
  switchMap,
  tap,
  filter,
  concatMap,
  defer,
  retry,
  throwError,
} from 'rxjs';
import {
  withReqState,
  setPropError,
  setPropLoading,
  setPropLoaded,
  setPropInit,
} from '@application/data-access';
import { ProfileService } from './infra/profile.service';
import {
  TActivity,
  TComment,
  TProfile,
  TResponsible,
  TPhone,
  TAddress,
  EPersonIncludeType,
  EPhoneType,
  ERegistrationType,
  TCommentToggleRead,
  TTimelineEvent,
  TGraduationList,
  TGraduation,
  TSimpleLoginCode,
  TPersonIntegration,
  TPersonLookupSummary,
  TPaguelaAccount,
  TSchoolEnrollment,
  tabs,
  filterVisibleProfileTabs,
  applySavedTabOrder,
  filterVisibleProfileMenuOptions,
  menuOptions,
  resolveProfileHeaderActionAppSource,
} from './entities/profile.model';
import { TOwnerSettingsByTenant } from './entities/owner-settings-by-tenant.model';
import {
  isUsableTenantId,
  tenantSetupOnConvert,
  NEW_TENANT_PLATFORM_SUBSCRIPTION_VERSION,
  TENANT_OWNER_SETUP_RETRY_DELAY_MS,
  TTenantOwnerSetup,
} from './entities/tenant-owner-setup.model';
import { reportTenantOwnerSetupFailure } from './tenant-owner-setup-report';
import { IVerifyEmail, UserService } from '@domains/person/user/data';
import { AlertStore } from '@alert/data-access';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorStore } from '@application/errors/data-access';
import { IFile } from '@shared/filegroup/data-access';
import {
  APP_CONFIG,
  AppVersion,
  isLegacyAppVersion,
  resolveEffectiveAppSource,
} from '@application/config';
import { DataSource } from 'devextreme/common/data';
import {
  PermissionNumber,
  SelfUserStore,
  SystemModulesStore,
} from '@domains/person/user/self/data';
import { DateTime } from 'luxon';
import { DialogStore } from '@shared/components/ui/dialog/data-access';
import {
  AccessPointEdgeService,
  PersonNounService,
} from '@settings/data-access';
import { TCategory, TCategoryHistory } from '@settings/data-access';
import {
  CreditAssessmentPF,
  CreditAssessmentPFAll,
  CreditAssessmentPJ,
  CreditAssessmentPJAll,
} from './entities/credit-assessment.model';
import { TPersonProduct } from '@consumption/data-access';
import { RouterStore } from '@route/data-access';
import { EPaguelaSituation } from '@financial/data-access';
import { UiStore } from '@theme/data-access';
import { getDateTime, normalizeId } from '@shared/utils';
import { AccountService, TGroup } from '@account/data-access';
import { AuthStore } from '@auth/data-access';
import { PersonService } from '@domains/person/register/data';

const TEMP_AUTH_PERMISSION_GUARD_PATH = 'TEMPORARY_AUTH_MODAL.PERMISSION_GUARD';

/**
 * O `Email` normalizado, ou string vazia quando não sobra nada depois do trim.
 *
 * Um e-mail só com espaços é `truthy`, por isso atravessava todos os guards
 * `if (email)` dos ecrãs e chegava ao backend como `?email=%20`, que responde
 * HTTP 400 "email is required" — um por cada abertura do perfil de quem tem o
 * campo assim preenchido. O utilizador não via nada (o erro apenas apagava o
 * selo de e-mail verificado), mas cada 400 desses vira uma issue no Sentry.
 *
 * O guard vive aqui, e não em cada `checkEmail()`, porque este é o último ponto
 * antes da rede: o botão "verificar novamente" do `profile-main-info` chama o
 * store direto da template, sem componente pelo meio para o filtrar.
 */
/** Ids reach the store from the route and from API bodies, which may differ in casing. */
const isSamePerson = (
  left: string | null | undefined,
  right: string | null | undefined
): boolean => !!left && !!right && left.toLowerCase() === right.toLowerCase();

const normalizeEmail = (email: string | null | undefined): string =>
  email?.trim() ?? '';

type TPermissionAction = 'create' | 'update' | 'view' | 'delete';

interface IPermissionRequestConfig {
  permissions: number[];
  translationKey: string;
}

export interface IProfileStore {
  profile: TProfile;
  profileEdit: TProfile;
  profileActivities: TActivity[];
  registrationType: ERegistrationType;
  isNewProfile: boolean;
  personId?: string;
  responsible?: TResponsible;
  lastComment?: TComment;
  activities?: TActivity[];
  showProfileEditModal?: boolean;
  actionOnSaveProfileModal?: (personId: string) => void;
  showViewCommentModal?: boolean;
  showEditCommentModal?: boolean;
  group: TGroup;
  openComment?: TComment;
  removedCommentId: string;
  toggleRead?: TCommentToggleRead;
  documentShowEditModal: boolean;
  documentShowViewModal: boolean;
  documentShowAddModal: boolean;
  documentFile: IFile;
  receivementSummary: number;
  accountsReceivablesSummary: number;
  openMonthByPerson?: number;
  simpleLoginCode: TSimpleLoginCode;
  contractInfo: string;
  VerifyEmailState: Record<string, 'init' | 'loading' | 'loaded' | 'error'>;
  VerifyEmailResponse: IVerifyEmail;
  ReVerifyEmailState: Record<string, 'init' | 'loading' | 'loaded' | 'error'>;
  ReVerifyEmailResponse: IVerifyEmail;
  email: {
    isValid?: boolean;
    exists?: boolean;
  };
  /** True when temporary password is blocked for the current form email (API returned false from Verification/IsPossibleToSetTemporaryEmail). Null while loading. */
  canSetTemporaryEmail: boolean | null;
  accessCd: {
    exists?: boolean;
  };
  cpf: {
    exists?: boolean;
    code?: string;
  };
  fullName: {
    exists?: boolean;
    code?: string;
  };
  company: {
    fantasyName?: string;
    legalBusinessName?: string;
  };
  AccessFrequency: TAccessLog[];
  phones?: TPhone[];
  addresses?: TAddress[];
  TimelineNextDate: string;
  TimelineEvents: TTimelineEvent[];
  graduation: Partial<TGraduation>;
  graduations?: TGraduationList[];
  showGraduationModal: boolean;
  personCategoryHistory?: TCategoryHistory[];
  personCategory?: TCategory;
  allCreditAssessmentPJ?: CreditAssessmentPJAll[];
  creditAssessmentPJ?: CreditAssessmentPJ;
  allCreditAssessmentPF?: CreditAssessmentPFAll[];
  creditAssessmentPF?: CreditAssessmentPF;
  integration?: TPersonIntegration;
  profileProducts: TPersonProduct[];
  paguelaAccount?: TPaguelaAccount;
  /** Result of last access simulation: true = allowed, false = denied. */
  simulateAccessAllowed: boolean | null;
  /** Message from SimulateAccess API (e.g. denial reason). */
  simulateAccessMessage: string | null;
  /** ISO timestamp of last simulation for humanized title. */
  lastSimulatedAt: string | null;
  /** ISO timestamp of person's last granted entry from AccessLogs. */
  lastAccessAt: string | null;
  /** Owner settings of the person in the edit form. Read by `getOwnerSettings`. */
  ownerSettings: TOwnerSettingsByTenant;
  /** Display names for linked persons in ties grid, keyed by TieId (linked PersonId). Filled via loadTiePersonDisplayNames. */
  tiePersonDisplayNames: Record<string, string>;
  /** Contact facts of the same linked persons, keyed by PersonId. Filled by the same read. */
  tiePersonContacts: Record<string, TTiePersonContact>;
  tiePersonDisplayNamesLoading: boolean;
  personsLookupById: Record<string, TPersonLookupSummary>;
  /** Active school enrollments for the current person (Turma/Série). Empty for non-school tenants. */
  schoolEnrollments: TSchoolEnrollment[];
}

/**
 * How a linked person can be reached — read through `GetPersonWithIncludes`,
 * the only person read that actually carries the phone collection. The e-mail
 * matters on its own: it is the app login.
 */
export type TTiePersonContact = {
  Email: string | null;
  HasPhone: boolean;
  HasMobilePhone: boolean;
};

const MOBILE_PHONE_TYPES: ReadonlySet<EPhoneType> = new Set([
  EPhoneType.Mobile,
  EPhoneType.MobileNotification,
]);

export function hasMobilePhone(phones: TPhone[] | null | undefined): boolean {
  return (phones ?? []).some(
    (phone) => phone?.Type !== null && phone?.Type !== undefined && MOBILE_PHONE_TYPES.has(phone.Type)
  );
}

export function mobilePhoneNumber(phones: TPhone[] | null | undefined): string {
  const mobile = (phones ?? []).find(
    (phone) => phone?.Type !== null && phone?.Type !== undefined && MOBILE_PHONE_TYPES.has(phone.Type)
  );
  return mobile?.Number ?? mobile?.FullNumber ?? '';
}

export type TAccessLog = {
  AccessOnUtc: string;
  AccessLogId: string;
};

export const initialProfileStore: IProfileStore = {
  profile: null,
  profileEdit: null,
  profileActivities: null,
  personId: null,
  registrationType: null,
  isNewProfile: false,
  responsible: null,
  lastComment: null,
  activities: null,
  showProfileEditModal: false,
  actionOnSaveProfileModal: null,
  showViewCommentModal: false,
  showEditCommentModal: false,
  group: null,
  openComment: null,
  removedCommentId: null,
  toggleRead: null,
  documentShowEditModal: false,
  documentShowViewModal: false,
  documentShowAddModal: false,
  documentFile: null,
  receivementSummary: null,
  accountsReceivablesSummary: null,
  openMonthByPerson: null,
  contractInfo: null,
  simpleLoginCode: null,
  VerifyEmailState: {},
  VerifyEmailResponse: null,
  ReVerifyEmailState: {},
  ReVerifyEmailResponse: null,
  email: {
    isValid: false,
    exists: false,
  },
  canSetTemporaryEmail: null,
  accessCd: {
    exists: false,
  },
  cpf: {
    exists: false,
  },
  fullName: {
    exists: false,
  },
  company: {
    fantasyName: null,
    legalBusinessName: null,
  },
  phones: [],
  addresses: [],
  AccessFrequency: [],
  TimelineNextDate: null,
  TimelineEvents: [],
  graduations: [],
  graduation: null,
  showGraduationModal: false,
  personCategoryHistory: null,
  personCategory: null,
  allCreditAssessmentPJ: null,
  creditAssessmentPJ: null,
  allCreditAssessmentPF: null,
  creditAssessmentPF: null,
  integration: null,
  profileProducts: null,
  paguelaAccount: null,
  simulateAccessAllowed: null,
  simulateAccessMessage: null,
  lastSimulatedAt: null,
  lastAccessAt: null,
  ownerSettings: null,
  tiePersonDisplayNames: {},
  tiePersonContacts: {},
  tiePersonDisplayNamesLoading: false,
  personsLookupById: {},
  schoolEnrollments: [],
};

export const ProfileStore = signalStore(
  { providedIn: 'root' },
  withState<IProfileStore>(initialProfileStore),
  withComputed(
    (
      store,
      selfUserStore = inject(SelfUserStore),
      systemModulesStore = inject(SystemModulesStore),
      appConfig = inject(APP_CONFIG),
      uiStore = inject(UiStore)
    ) => ({
      /**
       * Whether the viewed client runs on the legacy app. Informative only —
       * it feeds the legacy banner and blocks nothing on the profile.
       *
       * The discriminator is the client's own `TProfile.AppVersion`, not the
       * session tenant's: in admin scope a client IS a tenant. Null counts as
       * `Old` (`isLegacyAppVersion`), which is only safe inside admin scope —
       * outside it the field is always null and every person would read as
       * legacy. The `!!profile` guard keeps the banner from flashing before
       * the profile answers.
       */
      isLegacyClient: computed(() => {
        const profile = store.profile();
        return (
          selfUserStore.isAdminScope() &&
          !!profile &&
          isLegacyAppVersion(profile.AppVersion)
        );
      }),
      isGuardianTieSide: computed(() => {
        const profile = store.profile();
        return (
          !!profile &&
          (!!profile.IsResponsible ||
            profile.RegistrationType !== ERegistrationType.Customer)
        );
      }),
      tieSideResolved: computed(() => {
        const profileId = normalizeId(store.profile()?.PersonId)?.toLowerCase();
        const personId = normalizeId(store.personId())?.toLowerCase();
        return !!profileId && profileId === personId;
      }),
      profileHeaderActionOptions: computed(() => {
        const profile = store.profile();

        if (!profile) {
          return [];
        }

        return filterVisibleProfileMenuOptions(
          menuOptions({
            enabledPlatformFeatures: appConfig.enabledPlatformFeatures,
            isAdmin: selfUserStore.isAdminScope(),
            isPaguelaAdmin: selfUserStore.isPaguelaAdmin(),
            isToletusAdmin: selfUserStore.isToletusAdmin(),
            registrationType: profile.RegistrationType,
            appSource: resolveEffectiveAppSource(appConfig),
            isResponsible: profile.IsResponsible,
          }) || [],
          appConfig.enabledPlatformFeatures,
          (moduleId) => systemModulesStore.isModuleAllowed(moduleId),
          (moduleIds) => systemModulesStore.isAnyModuleAllowed(moduleIds)
        );
      }),
      profileHeaderActionAppSource: computed(() =>
        resolveProfileHeaderActionAppSource(
          appConfig,
          selfUserStore.isPaguelaAdmin()
        )
      ),
      profileTabs: computed(() => {
        const profile = store.profile();

        if (!profile) {
          return [];
        }

        const visible = filterVisibleProfileTabs(
          tabs({
            isAdmin: selfUserStore.isAdminScope(),
            isPaguelaAdmin: selfUserStore.isPaguelaAdmin(),
            hasPaguela: !!selfUserStore.personDataAsync()?.PagueLaId,
            registrationType: profile.RegistrationType,
            isMobile: uiStore.isMobile(),
          }) || [],
          appConfig.enabledPlatformFeatures,
          (moduleId) => systemModulesStore.isModuleAllowed(moduleId),
          (moduleIds) => systemModulesStore.isAnyModuleAllowed(moduleIds),
          (permissions) => selfUserStore.hasClientPermission(permissions)
        );

        return applySavedTabOrder(
          visible,
          selfUserStore.uiClientSettings()?.ProfileTabsOrder
        );
      }),
    })
  ),
  withMethods(
    (
      store,
      profileService = inject(ProfileService),
      accessPointEdgeService = inject(AccessPointEdgeService),
      routerStore = inject(RouterStore),
      userService = inject(UserService),
      alertStore = inject(AlertStore),
      personNounService = inject(PersonNounService),
      errorStore = inject(ErrorStore),
      dxOdataStoreService = inject(DxOdataStoreService),
      selfUserStore = inject(SelfUserStore),
      url = inject(APP_CONFIG).apiUrl,
      dialogStore = inject(DialogStore),
      accountService = inject(AccountService),
      authStore = inject(AuthStore),
      personService = inject(PersonService)
    ) => {
      const resolveRegistrationType = (
        registrationType?: ERegistrationType | null
      ): ERegistrationType | null => {
        if (registrationType !== null && registrationType !== undefined) {
          return registrationType;
        }

        const storeRegistrationType = store.registrationType?.();
        if (
          storeRegistrationType !== null &&
          storeRegistrationType !== undefined
        ) {
          return storeRegistrationType;
        }

        const currentProfile = store.profile?.();
        if (
          currentProfile?.RegistrationType !== null &&
          currentProfile?.RegistrationType !== undefined
        ) {
          return currentProfile.RegistrationType;
        }

        const currentProfileEdit = store.profileEdit?.();
        if (
          currentProfileEdit?.RegistrationType !== null &&
          currentProfileEdit?.RegistrationType !== undefined
        ) {
          return currentProfileEdit.RegistrationType;
        }

        return null;
      };

      const buildPermissionRequests = (
        action: TPermissionAction,
        registrationType?: ERegistrationType | null
      ): IPermissionRequestConfig[] => {
        const resolvedType = resolveRegistrationType(registrationType);

        const customerPermissions: Record<
          TPermissionAction,
          IPermissionRequestConfig
        > = {
          create: {
            permissions: [PermissionNumber.ClienteAdicionar],
            translationKey: 'CUSTOMER_CREATE',
          },
          update: {
            permissions: [PermissionNumber.ClienteAtualizar],
            translationKey: 'CUSTOMER_UPDATE',
          },
          view: {
            permissions: [PermissionNumber.ClienteObterPorId],
            translationKey: 'CUSTOMER_VIEW',
          },
          delete: {
            permissions: [PermissionNumber.ClienteRemover],
            translationKey: 'CUSTOMER_DELETE',
          },
        };

        const contactPermissions: Record<
          TPermissionAction,
          IPermissionRequestConfig
        > = {
          create: {
            permissions: [PermissionNumber.ContatoAdicionar],
            translationKey: 'CONTACT_CREATE',
          },
          update: {
            permissions: [PermissionNumber.ContatoAtualizar],
            translationKey: 'CONTACT_UPDATE',
          },
          view: {
            permissions: [PermissionNumber.ContatoObterPorId],
            translationKey: 'CONTACT_VIEW',
          },
          delete: {
            permissions: [PermissionNumber.ContatoRemover],
            translationKey: 'CONTACT_DELETE',
          },
        };

        const visitorPermissions: Record<
          TPermissionAction,
          IPermissionRequestConfig
        > = {
          create: {
            permissions: [PermissionNumber.VisitanteAdicionar],
            translationKey: 'VISITOR_CREATE',
          },
          update: {
            permissions: [PermissionNumber.VisitanteAtualizar],
            translationKey: 'VISITOR_UPDATE',
          },
          view: {
            permissions: [PermissionNumber.VisitanteObterPorId],
            translationKey: 'VISITOR_VIEW',
          },
          delete: {
            permissions: [PermissionNumber.VisitanteRemover],
            translationKey: 'VISITOR_DELETE',
          },
        };

        const requests: IPermissionRequestConfig[] = [];

        const pushRequest = (config?: IPermissionRequestConfig) => {
          if (config) {
            requests.push(config);
          }
        };

        switch (resolvedType) {
          case ERegistrationType.Customer:
            pushRequest(customerPermissions[action]);
            break;
          case ERegistrationType.Contact:
            pushRequest(contactPermissions[action]);
            break;
          case ERegistrationType.Visitor:
            pushRequest(visitorPermissions[action]);
            break;
          default:
            pushRequest(customerPermissions[action]);
            pushRequest(contactPermissions[action]);
            pushRequest(visitorPermissions[action]);
            break;
        }

        return requests;
      };

      /**
       * The app to write: the one given, or — for `current` — the one the
       * tenant already has. A tenant with no OwnerSettings (204) or whose
       * read the account service cannot answer for lack of a login (404) has
       * none, and gets the new app. Any other failure is the setup's failure:
       * guessing the new app there could overwrite a legacy one.
       */
      const resolveSetupAppVersion = (
        setup: TTenantOwnerSetup
      ): Observable<AppVersion> =>
        setup.appVersion !== 'current'
          ? of(setup.appVersion)
          : profileService.getOwnerSettingsByTenantIdAsync(setup.tenantId).pipe(
              map((ownerSettings) => ownerSettings?.AppVersion ?? AppVersion.New),
              catchError((error: HttpErrorResponse) =>
                error?.status === 404 ? of(AppVersion.New) : throwError(() => error)
              )
            );

      /**
       * The OwnerSettings writes owed by a conversion (`TTenantOwnerSetup`).
       * They run after the person is saved, so the conversion is already
       * done — and closed — when they start.
       *
       * The PUTs are idempotent: on any failure the whole attempt (the read of
       * the current app included) is repeated once, after a short pause. A failure that survives the retry leaves the academy
       * without app or platform subscription, which nothing else repairs yet
       * (no backend reconciliation), so it is surfaced twice: an alert for the
       * user, and an event of its own in Sentry to be mapped and fixed there.
       * The HTTP interceptor already reports each failed request; this event
       * is the one that says an academy was left incomplete.
       *
       * `concatMap` keeps a second academy saved meanwhile from cancelling the
       * first one's writes.
       */
      const setupTenantOwnerSettings = rxMethod<TTenantOwnerSetup>(
        pipe(
          filter((setup) => isUsableTenantId(setup.tenantId)),
          tap(() =>
            patchState(store, { ...setPropLoading('setupTenantOwnerSettings') })
          ),
          concatMap((setup) =>
            // `defer`: each attempt, the retry included, issues its requests anew.
            defer(() =>
              resolveSetupAppVersion(setup).pipe(
                switchMap((appVersion) =>
                  forkJoin([
                    profileService.updateAppVersionAsync(
                      setup.tenantId,
                      String(appVersion)
                    ),
                    ...(setup.platformSubscription
                      ? [
                          profileService.updatePlatformSubscriptionVersionAsync(
                            setup.tenantId,
                            NEW_TENANT_PLATFORM_SUBSCRIPTION_VERSION
                          ),
                        ]
                      : []),
                  ])
                )
              )
            ).pipe(
              retry({ count: 1, delay: TENANT_OWNER_SETUP_RETRY_DELAY_MS }),
              tapResponse({
                next: () => {
                  patchState(store, {
                    ...setPropLoaded('setupTenantOwnerSettings'),
                  });
                },
                error: (error: HttpErrorResponse) => {
                  patchState(store, {
                    ...setPropError('setupTenantOwnerSettings', error),
                  });
                  alertStore.create({
                    title: 'ALERT.TITLE_ERROR',
                    message: 'PROFILE.EDIT.TENANT_SETUP_ERROR.MESSAGE',
                    type: 'danger',
                  });
                  reportTenantOwnerSetupFailure(setup, error);
                },
              })
            )
          )
        )
      );

      /**
       * Converting a visitor into a customer makes it an academy: it owes the
       * platform subscription, and keeps the app it already has
       * (`tenantSetupOnConvert`).
       */
      const setupConvertedTenant = (personId: string) => {
        if (selfUserStore.isAdminScope()) {
          setupTenantOwnerSettings(tenantSetupOnConvert(personId));
        }
      };

      /**
       * A refused profile save (feature 003). Nothing was written in either case:
       * - 422 "Esta ficha tem plano em andamento (…)": the backend message, under
       *   "Não foi possível salvar o perfil" (frame T);
       * - 503 `dependency-unavailable`: the subscriptions could not be checked —
       *   own text, distinct from the router's alert (frame S, pending fix #12).
       * Anything else keeps the usual alert.
       */
      const reportUpdatePersonError = (error: HttpErrorResponse): void => {
        const title = 'PROFILE.EDIT.SAVE_ERROR.TITLE';
        if (error?.status === 422) {
          errorStore.handleError({ error, title });
          return;
        }
        if (
          error?.status === 503 &&
          error?.error?.Code === 'dependency-unavailable'
        ) {
          errorStore.handleError({
            title,
            message: 'PROFILE.EDIT.SAVE_ERROR.DEPENDENCY_UNAVAILABLE',
          });
          return;
        }
        errorStore.setHttpError(error);
      };

      const ensurePermissionForAction = async (
        action: TPermissionAction,
        registrationType?: ERegistrationType | null
      ): Promise<boolean> => {
        const requests = buildPermissionRequests(action, registrationType);

        if (!requests.length) {
          return true;
        }

        for (const request of requests) {
          const hasPermission = await selfUserStore.ensurePermissions(
            request.permissions,
            { interactive: false }
          );
          if (hasPermission) {
            return true;
          }
        }

        for (const request of requests) {
          const hasPermission = await selfUserStore.ensurePermissions(
            request.permissions,
            {
              title: personNounService.instant(
                `${TEMP_AUTH_PERMISSION_GUARD_PATH}.${request.translationKey}.TITLE`
              ),
              message: personNounService.instant(
                `${TEMP_AUTH_PERMISSION_GUARD_PATH}.${request.translationKey}.MESSAGE`
              ),
            }
          );

          if (hasPermission) {
            return true;
          }
        }

        return false;
      };

      const deleteGraduationById = rxMethod<string>(
        pipe(
          tap(() =>
            patchState(store, { ...setPropLoading('deleteGraduationById') })
          ),
          switchMap((id) =>
            profileService.deleteGraduationById(id).pipe(
              tapResponse({
                next: () => {
                  patchState(store, {
                    ...setPropLoaded('deleteGraduationById'),
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, {
                    ...setPropError('deleteGraduationById', error),
                  });
                },
              })
            )
          )
        )
      );

      /**
       * Writes a tenant's app (`OwnerSettings`). Declared out here because
       * `updatePerson` chains it after a successful save.
       */
      const updateAppVersionAsync = rxMethod<{
        tenantId: string;
        appVersion: string;
      }>(
        pipe(
          tap(() =>
            patchState(store, { ...setPropLoading('updateAppVersionAsync') })
          ),
          switchMap(({ tenantId, appVersion }) =>
            profileService.updateAppVersionAsync(tenantId, appVersion).pipe(
              tapResponse({
                next: () => {
                  patchState(store, {
                    ...setPropLoaded('updateAppVersionAsync'),
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, {
                    ...setPropError('updateAppVersionAsync', error),
                  });
                },
              })
            )
          )
        )
      );

      /**
       * Writes which access control (legacy or new) a tenant runs on the
       * legacy app (`OwnerSettings`). Declared out here because
       * `updatePerson` chains it after a successful save.
       */
      const updateUseNewAccessControl = rxMethod<{
        tenantId: string;
        useNewAccessControl: boolean;
      }>(
        pipe(
          tap(() =>
            patchState(store, {
              ...setPropLoading('updateUseNewAccessControl'),
            })
          ),
          switchMap(({ tenantId, useNewAccessControl }) =>
            profileService
              .updateActuarLegacyUseNewAccessControl(
                tenantId,
                useNewAccessControl
              )
              .pipe(
                tapResponse({
                  next: () => {
                    patchState(store, {
                      ...setPropLoaded('updateUseNewAccessControl'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, {
                      ...setPropError('updateUseNewAccessControl', error),
                    });
                  },
                })
              )
          )
        )
      );

      return {
        clearPersonData: () => patchState(store, initialProfileStore),
        blankState: () => {
          patchState(store, { ...setPropInit('getProfile') });
        },

        /**
         * Persists the user's profile-tab order (by tab id) into UiClientSettings.
         * Called when the user drag-reorders the profile tab bar.
         */
        saveTabsOrder(orderedTabs: { id: string }[]): void {
          selfUserStore.patchUiClientSettings({
            ProfileTabsOrder: orderedTabs.map((tab) => tab.id),
          });
          selfUserStore.persistUiClientSettings();
        },

        blankCommentState: () => {
          patchState(store, {
            openComment: null,
            ...setPropInit(
              'addComment',
              'updateComment',
              'removeComment',
              'getComment'
            ),
          });
        },

        blankSchoolEnrollmentsState: () => {
          patchState(
            store,
            { schoolEnrollments: [] },
            setPropInit('getSchoolEnrollmentsByPerson')
          );
        },

        blankGraduationState: () => {
          patchState(
            store,
            { graduation: null, graduations: null },
            setPropInit(
              'deleteGraduationById',
              'getAllGraduationsByPersonId',
              'createGraduation',
              'updateGraduation'
            )
          );
        },

        setPersonId: (personId: string) => {
          patchState(store, { personId });
        },

        /**
         * Active-person picker. Defaults to customers and visitors; callers that
         * also reach staff (profile ties) pass `User` in as well.
         */
        createActivePersonLookupConfig: (
          excludedPersonId: string | null,
          registrationTypes: ERegistrationType[] = [
            ERegistrationType.Customer,
            ERegistrationType.Visitor,
          ]
        ) => ({
          url: 'odata_person_service',
          metadata: 'PersonsList',
          key: 'PersonId',
          params: {
            $filter: `(${registrationTypes
              .map((type) => `RegistrationType eq ${type}`)
              .join(' or ')}) and IsActive eq true${
              excludedPersonId ? ` and PersonId ne ${excludedPersonId}` : ''
            }`,
            $select: 'PersonId,FullDisplayName,FullName,Email',
            $orderby: 'FullDisplayName asc',
          },
        }),

        setProfileEdit: (profileEdit: TProfile) => {
          patchState(store, { profileEdit });
        },

        toggleProfileEditModal: (
          showProfileEditModal = !store.showProfileEditModal(),
          actionOnSaveProfileModal: (personId: string) => void = null
        ) => {
          patchState(store, { showProfileEditModal, actionOnSaveProfileModal });
        },

        toggleViewCommentModal: (
          showViewCommentModal = !store.showViewCommentModal()
        ) => {
          patchState(store, { showViewCommentModal });
        },

        toggleEditCommentModal: (
          showEditCommentModal = !store.showEditCommentModal()
        ) => {
          patchState(store, { showEditCommentModal });
        },

        setOpenComment: (openComment: TComment) => {
          patchState(store, { openComment });
        },

        setRegistrationType: (registrationType: ERegistrationType) => {
          patchState(store, { registrationType });
        },

        setIsNewProfile: (isNewProfile: boolean) => {
          patchState(store, { isNewProfile });
        },

        blankStateEditModal: () => {
          patchState(store, {
            profileEdit: null,
            ...setPropInit('createPerson', 'updatePerson', 'profileEdit'),
          });
        },

        updateDocumentConfig: (documentConfig: {
          documentShowEditModal?: boolean;
          documentShowViewModal?: boolean;
          documentShowAddModal?: boolean;
          documentFile?: IFile;
        }) => {
          patchState(store, documentConfig);
        },

        verifyEmail: rxMethod<{ Email: string; StateKey?: string }>(
          pipe(
            map(({ Email, StateKey }) => ({
              Email: normalizeEmail(Email),
              StateKey,
            })),
            filter(({ Email }) => !!Email),
            tap(({ StateKey }) =>
              patchState(
                store,
                StateKey
                  ? {
                      VerifyEmailState: {
                        ...store.VerifyEmailState(),
                        [StateKey]: 'loading',
                      },
                    }
                  : { ...setPropLoading('verifyEmail') }
              )
            ),
            switchMap(({ Email, StateKey }) =>
              userService.verifyEmail(Email).pipe(
                tapResponse({
                  next: (response) => {
                    if (StateKey) {
                      patchState(store, {
                        VerifyEmailResponse: response,
                        VerifyEmailState: {
                          ...store.VerifyEmailState(),
                          [StateKey]: 'loaded',
                        },
                      });
                      return;
                    }
                    patchState(store, {
                      email: { ...store.email(), isValid: response?.IsValid },
                      ...setPropLoaded('verifyEmail'),
                    });
                  },
                  error: (e: HttpErrorResponse) => {
                    patchState(
                      store,
                      StateKey
                        ? {
                            VerifyEmailState: {
                              ...store.VerifyEmailState(),
                              [StateKey]: 'error',
                            },
                          }
                        : { ...setPropError('verifyEmail', e) }
                    );
                  },
                })
              )
            )
          )
        ),

        reVerifyEmail: rxMethod<{ Email: string; StateKey?: string }>(
          pipe(
            map(({ Email, StateKey }) => ({
              Email: normalizeEmail(Email),
              StateKey,
            })),
            filter(({ Email }) => !!Email),
            tap(({ StateKey }) =>
              patchState(
                store,
                StateKey
                  ? {
                      ReVerifyEmailState: {
                        ...store.ReVerifyEmailState(),
                        [StateKey]: 'loading',
                      },
                    }
                  : { ...setPropLoading('reVerifyEmail') }
              )
            ),
            switchMap(({ Email, StateKey }) =>
              userService.reVerifyEmail(Email).pipe(
                tapResponse({
                  next: (response) => {
                    if (response.IsValid) {
                      alertStore.create({
                        message: 'PROFILE.MESSAGES.EMAIL_VERIFIED_SUCCESS',
                        title: 'COMPONENTS.ALERT.SUCCESS',
                        type: 'success',
                      });
                    } else {
                      alertStore.create({
                        message: 'PROFILE.MESSAGES.EMAIL_VERIFIED_UNVALID',
                        title: 'COMPONENTS.ALERT.ATTENTION',
                        type: 'warning',
                      });
                    }
                    if (StateKey) {
                      patchState(store, {
                        ReVerifyEmailResponse: response,
                        ReVerifyEmailState: {
                          ...store.ReVerifyEmailState(),
                          [StateKey]: 'loaded',
                        },
                      });
                      return;
                    }
                    patchState(store, {
                      email: { ...store.email(), isValid: response?.IsValid },
                      ...setPropLoaded('reVerifyEmail'),
                    });
                  },
                  error: (e: HttpErrorResponse) => {
                    patchState(
                      store,
                      StateKey
                        ? {
                            ReVerifyEmailState: {
                              ...store.ReVerifyEmailState(),
                              [StateKey]: 'error',
                            },
                          }
                        : { ...setPropError('reVerifyEmail', e) }
                    );
                    errorStore.setHttpError(e);
                  },
                })
              )
            )
          )
        ),

        isEmailAlreadyUsed: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('isEmailAlreadyUsed') })
            ),
            switchMap((email) =>
              userService.isEmailAlreadyUsed(email).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(store, {
                      email: { ...store.email(), exists: response },
                      ...setPropLoaded('isEmailAlreadyUsed'),
                    });
                  },
                  error: (error) => {
                    patchState(store, {
                      ...setPropError('isEmailAlreadyUsed', error),
                    });
                  },
                })
              )
            )
          )
        ),

        checkIsPossibleToSetTemporaryEmail: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, {
                ...setPropLoading('checkIsPossibleToSetTemporaryEmail'),
                canSetTemporaryEmail: null,
              })
            ),
            switchMap((email) =>
              userService.isPossibleToSetTemporaryEmail(email).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(store, {
                      canSetTemporaryEmail: !!response,
                      ...setPropLoaded('checkIsPossibleToSetTemporaryEmail'),
                    });
                  },
                  error: (error) => {
                    patchState(store, {
                      canSetTemporaryEmail: false,
                      ...setPropError(
                        'checkIsPossibleToSetTemporaryEmail',
                        error
                      ),
                    });
                  },
                })
              )
            )
          )
        ),

        loadProfile: rxMethod<void>(
          pipe(
            // Lê o id do estado, logo só pode correr depois de `setPersonId`.
            // Sem esta guarda a chamada saía com `personId: null` e o backend
            // recusava-a com 400 ("The request field is required"), deixando
            // ainda `getProfileError` setado para quem navegasse a seguir.
            filter(() => !!store.personId()),
            tap(() => patchState(store, { ...setPropLoading('getProfile') })),
            switchMap(() =>
              from(ensurePermissionForAction('view')).pipe(
                switchMap((hasPermission) => {
                  if (!hasPermission) {
                    patchState(store, {
                      ...setPropError(
                        'getProfile',
                        new Error('ALERT.ERROR_401')
                      ),
                    });
                    return EMPTY;
                  }

                  const includes = [
                    EPersonIncludeType.Phones,
                    EPersonIncludeType.Addresses,
                    EPersonIncludeType.Responsible,
                    EPersonIncludeType.UserConsolidated,
                    EPersonIncludeType.Preference,
                    EPersonIncludeType.Access,
                    EPersonIncludeType.HealthConsolidated,
                    EPersonIncludeType.HistoryConsolidated,
                    EPersonIncludeType.EventConsolidated,
                    EPersonIncludeType.Integration,
                    EPersonIncludeType.Ties,
                  ];

                  if (selfUserStore.isAdminScope()) {
                    includes.push(EPersonIncludeType.Tax);
                  }

                  return profileService
                    .getPersonWithSpecificIncludes(store.personId(), includes)
                    .pipe(
                      tapResponse({
                        next: (profile) => {
                          patchState(store, {
                            profile: profile ?? null,
                            registrationType:
                              profile?.RegistrationType ??
                              resolveRegistrationType(),
                            ...setPropLoaded('getProfile'),
                          });
                        },
                        error: (error) => {
                          patchState(store, {
                            profile: null,
                            ...setPropError('getProfile', error),
                          });
                        },
                      })
                    );
                })
              )
            )
          )
        ),

        loadProfileEdit: rxMethod<string>(
          pipe(
            filter((personId) => !!personId),
            tap(() => patchState(store, { ...setPropLoading('profileEdit') })),
            switchMap((personId) =>
              from(ensurePermissionForAction('view')).pipe(
                switchMap((hasPermission) => {
                  if (!hasPermission) {
                    patchState(store, {
                      ...setPropError(
                        'profileEdit',
                        new Error('ALERT.ERROR_401')
                      ),
                    });
                    return EMPTY;
                  }

                  const includes = [
                    EPersonIncludeType.Phones,
                    EPersonIncludeType.Addresses,
                    EPersonIncludeType.Responsible,
                    EPersonIncludeType.UserConsolidated,
                    EPersonIncludeType.Preference,
                    EPersonIncludeType.Access,
                    EPersonIncludeType.HealthConsolidated,
                    EPersonIncludeType.HistoryConsolidated,
                    EPersonIncludeType.EventConsolidated,
                    EPersonIncludeType.Ties,
                  ];

                  if (selfUserStore.isAdminScope()) {
                    includes.push(EPersonIncludeType.Tax);
                  }

                  return profileService
                    .getPersonWithSpecificIncludes(personId, includes)
                    .pipe(
                      tapResponse({
                        next: (profileEdit) => {
                          patchState(store, {
                            profileEdit,
                            registrationType:
                              profileEdit?.RegistrationType ??
                              resolveRegistrationType(),
                            ...setPropLoaded('profileEdit'),
                          });
                        },
                        error: (error) => {
                          patchState(store, {
                            ...setPropError('profileEdit', error),
                          });
                        },
                      })
                    );
                })
              )
            )
          )
        ),

        getCommentsDataSource: (): DataSource => {
          return dxOdataStoreService.fromOdata({
            url: `${url.odata_person_service}`,
            key: 'CommentId',
            metadata: 'Comments',
            params: {
              targetPersonId: store.personId(),
            },
          });
        },

        getLastComment: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('getLastComment') })
            ),
            switchMap((personId) =>
              profileService.getLastComment(personId).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(store, {
                      lastComment: response?.value?.[0] ?? null,
                      ...setPropLoaded('getLastComment'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(store, {
                      lastComment: null,
                      ...setPropError('getLastComment', error),
                    });
                  },
                })
              )
            )
          )
        ),

        getOwnerSettings: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('getOwnerSettings') })
            ),
            switchMap((tenantId) =>
              profileService.getOwnerSettingsByTenantIdAsync(tenantId).pipe(
                tapResponse({
                  next: (ownerSettings) => {
                    patchState(store, {
                      ownerSettings,
                      ...setPropLoaded('getOwnerSettings'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(store, {
                      ownerSettings: null,
                      ...setPropError('getOwnerSettings', error),
                    });
                  },
                })
              )
            )
          )
        ),

        /**
         * Reads the person's active school enrollments (Turma/Série) from the
         * SchoolService OData endpoint. The read never errors on unresolved ids
         * (names come back `null`); on an HTTP failure we just clear the list so
         * the header indicator stays hidden — no error alert, since this is a
         * best-effort, school-only enrichment. Gate the call on the `school`
         * platform feature at the caller.
         */
        getSchoolEnrollmentsByPerson: rxMethod<string>(
          pipe(
            filter((personId) => !!personId),
            tap(() =>
              patchState(store, {
                ...setPropLoading('getSchoolEnrollmentsByPerson'),
              })
            ),
            switchMap((personId) =>
              profileService.getSchoolEnrollmentsByPerson(personId).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(store, {
                      schoolEnrollments: response?.value ?? [],
                      ...setPropLoaded('getSchoolEnrollmentsByPerson'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(store, {
                      schoolEnrollments: [],
                      ...setPropError('getSchoolEnrollmentsByPerson', error),
                    });
                  },
                })
              )
            )
          )
        ),

        updateAppVersionAsync,

        updateUseNewAccessControl,

        getComment: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { ...setPropLoading('getComment') })),
            switchMap((commentId) =>
              profileService.getComment(commentId).pipe(
                tapResponse({
                  next: (openComment) => {
                    patchState(store, {
                      openComment,
                      ...setPropLoaded('getComment'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, { ...setPropError('getComment', error) });
                  },
                })
              )
            )
          )
        ),

        toggleReadComment: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { ...setPropLoading('toggleRead') })),
            switchMap((commentId) =>
              profileService.toggleReadComment(commentId).pipe(
                tapResponse({
                  next: (toggleRead) => {
                    patchState(store, {
                      toggleRead,
                      ...setPropLoaded('toggleRead'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, { ...setPropError('toggleRead', error) });
                  },
                })
              )
            )
          )
        ),

        checkAccessCdExists: rxMethod<number>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('checkAccessCdExists') })
            ),
            switchMap((cd) =>
              profileService.checkAccessCdExists(cd).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(store, {
                      accessCd: { exists: response },
                      ...setPropLoaded('checkAccessCdExists'),
                    });
                  },
                  error: (error) => {
                    patchState(store, {
                      ...setPropError('checkAccessCdExists', error),
                    });
                  },
                })
              )
            )
          )
        ),

        checkCpfExists: rxMethod<string>(
          pipe(
            // `personsearch/search` responde 400 a um `q` vazio; não há busca a
            // fazer sem termo, por isso ela nem sai daqui.
            filter((cpf) => !!cpf?.trim()),
            tap(() =>
              patchState(store, { ...setPropLoading('checkCpfExists') })
            ),
            switchMap((cpf) =>
              profileService.searchPerson(cpf).pipe(
                tapResponse({
                  next: (response) => {
                    const value = response?.results;
                    const exists =
                      value?.length > 0 && cpf === value?.[0]?.fiscal_identity;
                    const code = value?.[0]?.friendly_code;

                    if (code !== store?.profileEdit()?.FriendlyCode) {
                      patchState(store, {
                        cpf: { exists, code },
                        ...setPropLoaded('checkCpfExists'),
                      });
                    }
                  },
                  error: (error) => {
                    patchState(store, {
                      ...setPropError('checkCpfExists', error),
                    });
                  },
                })
              )
            )
          )
        ),

        checkFullNameExists: rxMethod<string>(
          pipe(
            // Idem: um nome vazio (campos limpos no formulário de perfil) era o
            // que produzia os 400 de `?q=` no Sentry.
            filter((fullName) => !!fullName?.trim()),
            tap(() =>
              patchState(store, { ...setPropLoading('checkFullNameExists') })
            ),
            switchMap((fullName) =>
              profileService.searchPerson(fullName).pipe(
                tapResponse({
                  next: (response) => {
                    const value = response?.results;
                    const exists =
                      value?.length > 0 &&
                      fullName.toLowerCase() ===
                        value?.[0]?.full_name?.toLowerCase();
                    const code = value?.[0]?.friendly_code;

                    if (code !== store?.profileEdit()?.FriendlyCode) {
                      patchState(store, {
                        fullName: { exists, code },
                        ...setPropLoaded('checkFullNameExists'),
                      });
                    }
                  },
                  error: (error) => {
                    patchState(store, {
                      ...setPropError('checkFullNameExists', error),
                    });
                  },
                })
              )
            )
          )
        ),

        checkCompany: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { ...setPropLoading('checkCompany') })),
            switchMap((company) =>
              profileService.checkFiscalIdentity('cnpj', company).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(store, {
                      company: {
                        fantasyName: response?.['fantasia'],
                        legalBusinessName: response?.['nome'],
                      },
                      ...setPropLoaded('checkCompany'),
                    });
                  },
                  error: (error) => {
                    patchState(store, {
                      ...setPropError('checkCompany', error),
                    });
                  },
                })
              )
            )
          )
        ),

        createPerson: rxMethod<
          | TProfile
          | {
              person: TProfile;
              suppressSuccessAlert?: boolean;
              ownerAppVersion?: AppVersion | null;
            }
        >(
          pipe(
            tap(() => patchState(store, { ...setPropLoading('createPerson') })),
            switchMap((payloadInput) => {
              const hasConfig =
                typeof payloadInput === 'object' &&
                payloadInput !== null &&
                'person' in payloadInput;
              const config = hasConfig
                ? (payloadInput as {
                    person: TProfile;
                    suppressSuccessAlert?: boolean;
                    ownerAppVersion?: AppVersion | null;
                  })
                : null;
              const payload = config
                ? config.person
                : (payloadInput as TProfile);
              const suppressSuccessAlert = config?.suppressSuccessAlert === true;
              const ownerAppVersion = config?.ownerAppVersion ?? null;

              return from(
                ensurePermissionForAction('create', payload?.RegistrationType)
              ).pipe(
                switchMap((hasPermission) => {
                  if (!hasPermission) {
                    patchState(store, {
                      ...setPropError(
                        'createPerson',
                        new Error('ALERT.ERROR_401')
                      ),
                    });
                    return EMPTY;
                  }

                  const body =
                    ownerAppVersion == null
                      ? payload
                      : { ...payload, OwnerAppVersion: ownerAppVersion };

                  return profileService.createPerson(body).pipe(
                    tapResponse({
                      next: (profile) => {
                        if (!suppressSuccessAlert) {
                          alertStore.create({
                            message: 'PERSON.GENERAL.ADD_SUCCESSFULLY',
                            type: 'success',
                            title: 'ALERT.TITLE_SUCCESS',
                          });
                        }
                        patchState(store, {
                          profile,
                          registrationType:
                            profile?.RegistrationType ??
                            resolveRegistrationType(payload?.RegistrationType),
                          ...setPropLoaded('createPerson'),
                        });
                      },
                      error: (error: HttpErrorResponse) => {
                        errorStore.setHttpError(error);
                        patchState(store, {
                          ...setPropError('createPerson', error),
                        });
                      },
                    })
                  );
                })
              );
            })
          )
        ),

        /**
         * `appVersion` is passed when the edit also changed the tenant's app:
         * it lives in OwnerSettings, not in the person, so it is written only
         * once the person save (`PATCH /PersonWithIncludes`) succeeded.
         * `useNewAccessControl` follows the same rule for the tenant's access
         * control (legacy or new).
         */
        updatePerson: rxMethod<
          | Partial<TProfile>
          | {
              person: Partial<TProfile>;
              suppressSuccessAlert?: boolean;
              appVersion?: { tenantId: string; appVersion: string };
              useNewAccessControl?: {
                tenantId: string;
                useNewAccessControl: boolean;
              };
            }
        >(
          pipe(
            tap(() => patchState(store, { ...setPropLoading('updatePerson') })),
            switchMap((payloadInput) => {
              const hasConfig =
                typeof payloadInput === 'object' &&
                payloadInput !== null &&
                'person' in payloadInput;
              const payload = hasConfig
                ? (payloadInput as { person: Partial<TProfile> }).person
                : (payloadInput as Partial<TProfile>);
              const suppressSuccessAlert = hasConfig
                ? (payloadInput as { suppressSuccessAlert?: boolean })
                    .suppressSuccessAlert === true
                : false;
              const appVersionWrite = hasConfig
                ? (
                    payloadInput as {
                      appVersion?: { tenantId: string; appVersion: string };
                    }
                  ).appVersion ?? null
                : null;
              const useNewAccessControlWrite = hasConfig
                ? (
                    payloadInput as {
                      useNewAccessControl?: {
                        tenantId: string;
                        useNewAccessControl: boolean;
                      };
                    }
                  ).useNewAccessControl ?? null
                : null;

              return from(
                ensurePermissionForAction('update', payload?.RegistrationType)
              ).pipe(
                switchMap((hasPermission) => {
                  if (!hasPermission) {
                    patchState(store, {
                      ...setPropError(
                        'updatePerson',
                        new Error('ALERT.ERROR_401')
                      ),
                    });
                    return EMPTY;
                  }

                  const profile = store.profile();
                  const updatedProfile = { ...profile, ...payload };

                  delete updatedProfile?.DeletedOn;
                  delete updatedProfile?.DeletedBy;
                  delete updatedProfile?.FullDisplayName;

                  return profileService
                    .patchPersonWithIncludes(updatedProfile)
                    .pipe(
                      tapResponse({
                        next: (updatedProfileFromApi) => {
                          if (updatedProfileFromApi == null) {
                            patchState(store, {
                              ...setPropInit('updatePerson'),
                            });
                            // A save answered without a body still succeeded.
                            if (appVersionWrite) {
                              updateAppVersionAsync(appVersionWrite);
                            }
                            if (useNewAccessControlWrite) {
                              updateUseNewAccessControl(
                                useNewAccessControlWrite
                              );
                            }
                            return;
                          }
                          if (!suppressSuccessAlert) {
                            alertStore.create({
                              message: 'PERSON.GENERAL.UPDATE_SUCCESSFULLY',
                              type: 'success',
                              title: 'ALERT.TITLE_SUCCESS',
                            });
                          }
                          const currentRouteProfile = store.profile();
                          const payloadPersonId =
                            payload.PersonId ?? updatedProfileFromApi.PersonId;
                          if (
                            currentRouteProfile?.PersonId != null &&
                            payloadPersonId != null &&
                            payloadPersonId !== currentRouteProfile.PersonId
                          ) {
                            routerStore.navigate([
                              `/profile/${payloadPersonId}`,
                            ]);
                          }
                          patchState(store, {
                            profile: {
                              ...updatedProfileFromApi,
                              IsResponsible:
                                updatedProfile.IsResponsible ??
                                currentRouteProfile?.IsResponsible ??
                                updatedProfileFromApi.IsResponsible ??
                                null,
                            },
                            registrationType:
                              updatedProfileFromApi.RegistrationType ??
                              resolveRegistrationType(),
                            ...setPropLoaded('updatePerson'),
                          });

                          if (appVersionWrite) {
                            updateAppVersionAsync(appVersionWrite);
                          }
                          if (useNewAccessControlWrite) {
                            updateUseNewAccessControl(useNewAccessControlWrite);
                          }

                          // The profile header's "convert to customer". The
                          // edit modal is global, so the person saved has to be
                          // the one open, or the open one's type says nothing.
                          if (
                            isSamePerson(
                              profile?.PersonId,
                              updatedProfileFromApi.PersonId
                            ) &&
                            profile?.RegistrationType ===
                              ERegistrationType.Visitor &&
                            updatedProfileFromApi.RegistrationType ===
                              ERegistrationType.Customer
                          ) {
                            setupConvertedTenant(updatedProfileFromApi.PersonId);
                          }
                        },
                        error: (error: HttpErrorResponse) => {
                          reportUpdatePersonError(error);
                          patchState(store, {
                            ...setPropError('updatePerson', error),
                          });
                        },
                      })
                    );
                })
              );
            })
          )
        ),
        convertVisitor: rxMethod<string>(
          pipe(
            switchMap((PersonId) => {
              return profileService
                .patchPerson({
                  PersonId,
                  RegistrationType: ERegistrationType.Customer,
                })
                .pipe(
                  tapResponse({
                    next: () => {
                      alertStore.create({
                        title: 'COMPONENTS.ALERT.SUCCESS',
                        message: personNounService.instant(
                          'SHARED.PERSON_ALERTS.SUCCESS.VISITOR_CONVERTED'
                        ),
                        type: 'success',
                      });

                      setupConvertedTenant(PersonId);
                      routerStore.navigate(['profile', PersonId]);
                    },
                    error: (error: HttpErrorResponse) => {
                      errorStore.setHttpError(error);
                    },
                  })
                );
            })
          )
        ),
        convertContact: rxMethod<{
          PersonId: string;
          LegalBusinessName: string;
        }>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('convertContact') })
            ),
            switchMap((payload) => {
              return profileService
                .patchPerson({
                  PersonId: payload.PersonId,
                  LegalBusinessName: payload.LegalBusinessName,
                  RegistrationType: ERegistrationType.Supplier,
                })
                .pipe(
                  tapResponse({
                    next: () => {
                      alertStore.create({
                        title: 'COMPONENTS.ALERT.SUCCESS',
                        message:
                          'PROFILE.EDIT.CONVERT_TO_SUPPLIER_SUCCESSFULLY',
                        type: 'success',
                      });

                      routerStore.navigate(['profile', payload.PersonId]);
                      patchState(store, { ...setPropLoaded('convertContact') });
                    },
                    error: (error: HttpErrorResponse) => {
                      errorStore.setHttpError(error);
                      patchState(store, {
                        ...setPropError('convertContact', error),
                      });
                    },
                  })
                );
            })
          )
        ),
        patchPerson: rxMethod<Partial<TProfile>>(
          pipe(
            tap(() => patchState(store, { ...setPropLoading('patchPerson') })),
            switchMap((propertyPatch) => {
              const patchPerson = {
                PersonId: store.personId(),
                ...propertyPatch,
              };
              return profileService.patchPerson(patchPerson).pipe(
                tapResponse({
                  next: (updatedProfile) => {
                    patchState(store, {
                      profile: {
                        ...updatedProfile,
                        IsResponsible:
                          updatedProfile.IsResponsible ??
                          store.profile()?.IsResponsible ??
                          null,
                      },
                      ...setPropLoaded('patchPerson'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.handleError({
                      error,
                      title: 'PERSON.GENERAL.UPDATE_ERROR',
                    });
                    patchState(store, {
                      ...setPropError('patchPerson', error),
                    });
                  },
                })
              );
            })
          )
        ),

        loadResponsible: rxMethod<void>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('getResponsible') })
            ),
            switchMap(() =>
              profileService.getResponsible(store.personId()).pipe(
                tapResponse({
                  next: (responsible) => {
                    patchState(store, {
                      responsible,
                      ...setPropLoaded('getResponsible'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, {
                      ...setPropError('getResponsible', error),
                    });
                  },
                })
              )
            )
          )
        ),

        createResponsible: rxMethod<TResponsible>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('createResponsible') })
            ),
            switchMap((responsible) => {
              const responsibleData = {
                ...responsible,
                PersonId: store.personId(),
              };

              return profileService.createResponsible(responsibleData).pipe(
                tapResponse({
                  next: (createdResponsible) => {
                    alertStore.create({
                      message: 'PROFILE.ALERT.SUCCESS.RESPONSIBLE',
                      type: 'success',
                      title: 'COMPONENTS.ALERT.SUCCESS',
                    });
                    patchState(store, {
                      responsible: createdResponsible,
                      ...setPropLoaded('createResponsible'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.handleError({
                      error,
                      title: 'PROFILE.ALERT.ERROR.RESPONSIBLE_ADD',
                    });
                    patchState(store, {
                      ...setPropError('createResponsible', error),
                    });
                  },
                })
              );
            })
          )
        ),

        updateResponsible: rxMethod<TResponsible>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('updateResponsible') })
            ),
            switchMap((responsible) => {
              const responsibleData = {
                ...responsible,
                PersonId: store.personId(),
              };

              return profileService.updateResponsible(responsibleData).pipe(
                tapResponse({
                  next: (updatedResponsible) => {
                    alertStore.create({
                      message: 'Responsável atualizado com sucesso!',
                      type: 'success',
                      title: 'COMPONENTS.ALERT.SUCCESS',
                    });
                    patchState(store, {
                      responsible: updatedResponsible,
                      ...setPropLoaded('updateResponsible'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.handleError({
                      error,
                      title: 'PROFILE.ALERT.ERROR.RESPONSIBLE_EDIT',
                    });
                    patchState(store, {
                      ...setPropError('updateResponsible', error),
                    });
                  },
                })
              );
            })
          )
        ),

        patchResponsible: rxMethod<Partial<TResponsible>>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('patchResponsible') })
            ),
            switchMap((propertyPatch) => {
              const patchData = {
                PersonId: store.personId(),
                ...propertyPatch,
              };

              return profileService.patchResponsible(patchData).pipe(
                tapResponse({
                  next: (updatedResponsible) => {
                    patchState(store, {
                      responsible: updatedResponsible,
                      ...setPropLoaded('patchResponsible'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.handleError({
                      error,
                      title: 'PROFILE.ALERT.ERROR.RESPONSIBLE_EDIT',
                    });
                    patchState(store, {
                      ...setPropError('patchResponsible', error),
                    });
                  },
                })
              );
            })
          )
        ),

        deleteResponsible: rxMethod<void>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('deleteResponsible') })
            ),
            switchMap(() =>
              profileService.deleteResponsible(store.personId()).pipe(
                tapResponse({
                  next: () => {
                    alertStore.create({
                      message: 'Responsável removido com sucesso!',
                      type: 'success',
                      title: 'COMPONENTS.ALERT.SUCCESS',
                    });
                    patchState(store, {
                      responsible: null,
                      ...setPropLoaded('deleteResponsible'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.handleError({
                      error,
                      title: 'PROFILE.ALERT.ERROR.RESPONSIBLE_DELETE',
                    });
                    patchState(store, {
                      ...setPropError('deleteResponsible', error),
                    });
                  },
                })
              )
            )
          )
        ),

        addComment: rxMethod<TComment>(
          pipe(
            tap(() => patchState(store, { ...setPropLoading('addComment') })),
            switchMap((comment) =>
              profileService.adicionarComentario(comment).pipe(
                tapResponse({
                  next: () => {
                    patchState(store, { ...setPropLoaded('addComment') });
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(store, { ...setPropError('addComment', error) });
                  },
                })
              )
            )
          )
        ),

        updateComment: rxMethod<TComment>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('updateComment') })
            ),
            switchMap((comment) =>
              profileService.atualizarComentario(comment).pipe(
                tapResponse({
                  next: () => {
                    patchState(store, { ...setPropLoaded('updateComment') });
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(store, {
                      ...setPropError('updateComment', error),
                    });
                  },
                })
              )
            )
          )
        ),

        removeComment: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('removeComment') })
            ),
            switchMap((id) =>
              profileService.removerComentario(id).pipe(
                tapResponse({
                  next: () => {
                    patchState(store, {
                      removedCommentId: normalizeId(id),
                      ...setPropLoaded('removeComment'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(store, {
                      ...setPropError('removeComment', error),
                    });
                  },
                })
              )
            )
          )
        ),

        emailBlankState: () => {
          patchState(store, {
            email: { isValid: false, exists: false },
            canSetTemporaryEmail: null,
            ...setPropInit(
              'verifyEmail',
              'isEmailAlreadyUsed',
              'reVerifyEmail',
              'checkIsPossibleToSetTemporaryEmail'
            ),
          });
        },

        loadPhones: rxMethod<void>(
          pipe(
            tap(() => patchState(store, { ...setPropLoading('loadPhones') })),
            switchMap(() =>
              profileService.getByPerson(store.personId()).pipe(
                tapResponse({
                  next: (phones) => {
                    patchState(store, {
                      phones,
                      ...setPropLoaded('loadPhones'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.handleError({
                      error,
                      title: 'PROFILE.ALERT.PHONES.ERROR_PHONES_LOAD',
                    });
                    patchState(store, { ...setPropError('loadPhones', error) });
                  },
                })
              )
            )
          )
        ),

        loadPhone: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { ...setPropLoading('loadPhone') })),
            switchMap((id) =>
              profileService.getPhone(id).pipe(
                tapResponse({
                  next: (phone) => {
                    const phones = store.phones();
                    const updatedPhones = phones
                      ? phones.map((p) => (p.PhoneId === id ? phone : p))
                      : [phone];

                    patchState(store, {
                      phones: updatedPhones,
                      ...setPropLoaded('loadPhone'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.handleError({
                      error,
                      title: 'PROFILE.ALERT.PHONES.ERROR_PHONE_LOAD',
                    });
                    patchState(store, { ...setPropError('loadPhone', error) });
                  },
                })
              )
            )
          )
        ),

        createPhone: rxMethod<TPhone>(
          pipe(
            tap(() => patchState(store, { ...setPropLoading('createPhone') })),
            switchMap((phone) => {
              const phoneData = {
                ...phone,
                PersonId: store.personId(),
              };

              return profileService.createPhone(phoneData).pipe(
                tapResponse({
                  next: (createdPhone) => {
                    alertStore.create({
                      message: 'Telefone adicionado com sucesso!',
                      type: 'success',
                      title: 'COMPONENTS.ALERT.SUCCESS',
                    });

                    const phones = store.phones() || [];
                    patchState(store, {
                      phones: [...phones, createdPhone],
                      ...setPropLoaded('createPhone'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.handleError({
                      error,
                      title: 'PROFILE.ALERT.PHONES.ERROR_PHONE_ADD',
                    });
                    patchState(store, {
                      ...setPropError('createPhone', error),
                    });
                  },
                })
              );
            })
          )
        ),

        updatePhone: rxMethod<TPhone>(
          pipe(
            tap(() => patchState(store, { ...setPropLoading('updatePhone') })),
            switchMap((phone) => {
              const phoneData = {
                ...phone,
                PersonId: store.personId(),
              };

              return profileService.updatePhone(phoneData).pipe(
                tapResponse({
                  next: (updatedPhone) => {
                    alertStore.create({
                      message: 'Telefone atualizado com sucesso!',
                      type: 'success',
                      title: 'COMPONENTS.ALERT.SUCCESS',
                    });

                    const phones = store.phones();
                    const updatedPhones = phones
                      ? phones.map((p) =>
                          p.PhoneId === phone.PhoneId ? updatedPhone : p
                        )
                      : [updatedPhone];

                    patchState(store, {
                      phones: updatedPhones,
                      ...setPropLoaded('updatePhone'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.handleError({
                      error,
                      title: 'PROFILE.ALERT.PHONES.ERROR_PHONE_EDIT',
                    });
                    patchState(store, {
                      ...setPropError('updatePhone', error),
                    });
                  },
                })
              );
            })
          )
        ),

        patchPhone: rxMethod<Partial<TPhone>>(
          pipe(
            tap(() => patchState(store, { ...setPropLoading('patchPhone') })),
            switchMap((propertyPatch) => {
              const patchData = {
                ...propertyPatch,
                PersonId: store.personId(),
              };

              return profileService.patchPhone(patchData).pipe(
                tapResponse({
                  next: (updatedPhone) => {
                    const phones = store.phones();
                    const updatedPhones = phones
                      ? phones.map((p) =>
                          p.PhoneId === propertyPatch.PhoneId ? updatedPhone : p
                        )
                      : [updatedPhone];

                    patchState(store, {
                      phones: updatedPhones,
                      ...setPropLoaded('patchPhone'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.handleError({
                      error,
                      title: 'PROFILE.ALERT.PHONES.ERROR_PHONE_EDIT',
                    });
                    patchState(store, { ...setPropError('patchPhone', error) });
                  },
                })
              );
            })
          )
        ),

        deletePhone: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { ...setPropLoading('deletePhone') })),
            switchMap((id) =>
              profileService.deletePhone(id).pipe(
                tapResponse({
                  next: () => {
                    alertStore.create({
                      message: 'Telefone removido com sucesso!',
                      type: 'success',
                      title: 'COMPONENTS.ALERT.SUCCESS',
                    });

                    const phones = store.phones();
                    const updatedPhones = phones
                      ? phones.filter((p) => p.PhoneId !== id)
                      : [];

                    patchState(store, {
                      phones: updatedPhones,
                      ...setPropLoaded('deletePhone'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.handleError({
                      error,
                      title: 'PROFILE.ALERT.PHONES.ERROR_PHONE_DELETE',
                    });
                    patchState(store, {
                      ...setPropError('deletePhone', error),
                    });
                  },
                })
              )
            )
          )
        ),

        loadAddresses: rxMethod<void>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('loadAddresses') })
            ),
            switchMap(() =>
              profileService.getAddressByPerson(store.personId()).pipe(
                tapResponse({
                  next: (addresses) => {
                    patchState(store, {
                      addresses,
                      ...setPropLoaded('loadAddresses'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.handleError({
                      error,
                      title: 'PROFILE.ALERT.ADDRESSES.ERROR_ADDRESSES_LOAD',
                    });
                    patchState(store, {
                      ...setPropError('loadAddresses', error),
                    });
                  },
                })
              )
            )
          )
        ),

        loadAddress: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { ...setPropLoading('loadAddress') })),
            switchMap((id) =>
              profileService.getAddress(id).pipe(
                tapResponse({
                  next: (address) => {
                    const addresses = store.addresses();
                    const updatedAddresses = addresses
                      ? addresses.map((a) => (a.AddressId === id ? address : a))
                      : [address];

                    patchState(store, {
                      addresses: updatedAddresses,
                      ...setPropLoaded('loadAddress'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.handleError({
                      error,
                      title: 'PROFILE.ALERT.ADDRESSES.ERROR_ADDRESS_LOAD',
                    });
                    patchState(store, {
                      ...setPropError('loadAddress', error),
                    });
                  },
                })
              )
            )
          )
        ),

        createAddress: rxMethod<TAddress>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('createAddress') })
            ),
            switchMap((address) => {
              const addressData = {
                ...address,
                PersonId: store.personId(),
              };

              return profileService.createAddress(addressData).pipe(
                tapResponse({
                  next: (createdAddress) => {
                    alertStore.create({
                      message: 'Endereço adicionado com sucesso!',
                      type: 'success',
                      title: 'COMPONENTS.ALERT.SUCCESS',
                    });

                    const addresses = store.addresses() || [];
                    patchState(store, {
                      addresses: [...addresses, createdAddress],
                      ...setPropLoaded('createAddress'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.handleError({
                      error,
                      title: 'PROFILE.ALERT.ADDRESSES.ERROR_ADDRESS_ADD',
                    });
                    patchState(store, {
                      ...setPropError('createAddress', error),
                    });
                  },
                })
              );
            })
          )
        ),

        updateAddress: rxMethod<TAddress>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('updateAddress') })
            ),
            switchMap((address) => {
              const addressData = {
                ...address,
                PersonId: store.personId(),
              };

              return profileService.updateAddress(addressData).pipe(
                tapResponse({
                  next: (updatedAddress) => {
                    alertStore.create({
                      message: 'Endereço atualizado com sucesso!',
                      type: 'success',
                      title: 'COMPONENTS.ALERT.SUCCESS',
                    });

                    const addresses = store.addresses();
                    const updatedAddresses = addresses
                      ? addresses.map((a) =>
                          a.AddressId === address.AddressId ? updatedAddress : a
                        )
                      : [updatedAddress];

                    patchState(store, {
                      addresses: updatedAddresses,
                      ...setPropLoaded('updateAddress'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.handleError({
                      error,
                      title: 'PROFILE.ALERT.ADDRESSES.ERROR_ADDRESS_EDIT',
                    });
                    patchState(store, {
                      ...setPropError('updateAddress', error),
                    });
                  },
                })
              );
            })
          )
        ),

        patchAddress: rxMethod<Partial<TAddress>>(
          pipe(
            tap(() => patchState(store, { ...setPropLoading('patchAddress') })),
            switchMap((propertyPatch) => {
              const patchData = {
                ...propertyPatch,
                PersonId: store.personId(),
              };

              return profileService.patchAddress(patchData).pipe(
                tapResponse({
                  next: (updatedAddress) => {
                    const addresses = store.addresses();
                    const updatedAddresses = addresses
                      ? addresses.map((a) =>
                          a.AddressId === propertyPatch.AddressId
                            ? updatedAddress
                            : a
                        )
                      : [updatedAddress];

                    patchState(store, {
                      addresses: updatedAddresses,
                      ...setPropLoaded('patchAddress'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.handleError({
                      error,
                      title: 'PROFILE.ALERT.ADDRESSES.ERROR_ADDRESS_EDIT',
                    });
                    patchState(store, {
                      ...setPropError('patchAddress', error),
                    });
                  },
                })
              );
            })
          )
        ),

        deleteAddress: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('deleteAddress') })
            ),
            switchMap((id) =>
              profileService.deleteAddress(id).pipe(
                tapResponse({
                  next: () => {
                    alertStore.create({
                      message: 'Endereço removido com sucesso!',
                      type: 'success',
                      title: 'COMPONENTS.ALERT.SUCCESS',
                    });

                    const addresses = store.addresses();
                    const updatedAddresses = addresses
                      ? addresses.filter((a) => a.AddressId !== id)
                      : [];

                    patchState(store, {
                      addresses: updatedAddresses,
                      ...setPropLoaded('deleteAddress'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.handleError({
                      error,
                      title: 'PROFILE.ALERT.ADDRESSES.ERROR_ADDRESS_DELETE',
                    });
                    patchState(store, {
                      ...setPropError('deleteAddress', error),
                    });
                  },
                })
              )
            )
          )
        ),

        getAccessFrequency: rxMethod<{ startOn: string; endOn: string }>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('getAccessFrequency') })
            ),
            switchMap(({ startOn, endOn }) =>
              profileService
                .getAccessFrequency(store.personId(), startOn, endOn)
                .pipe(
                  tapResponse({
                    next: (response) => {
                      patchState(store, {
                        AccessFrequency: response?.value || [],
                        ...setPropLoaded('getAccessFrequency'),
                      });
                    },
                    error: (error) => {
                      patchState(store, {
                        ...setPropError('getAccessFrequency', error),
                      });
                    },
                  })
                )
            )
          )
        ),

        getLastAccess: rxMethod<string>(
          pipe(
            filter((personId) => !!personId),
            switchMap((personId) =>
              profileService.getLastAccess(personId).pipe(
                tapResponse({
                  next: (response) => {
                    const value = response?.value;
                    const lastAccessAt = value?.length
                      ? value[0].AccessOnUtc
                      : null;
                    patchState(store, { lastAccessAt });
                  },
                  error: () => {
                    patchState(store, { lastAccessAt: null });
                  },
                })
              )
            )
          )
        ),

        getTotalReceivementDailySummaryByPersonId: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, {
                ...setPropLoading('getTotalReceivementDailySummaryByPersonId'),
              })
            ),
            switchMap((personId) =>
              profileService
                .getTotalReceivementDailySummaryByPersonId(personId)
                .pipe(
                  tapResponse({
                    next: (response) => {
                      patchState(store, {
                        receivementSummary: response,
                        ...setPropLoaded(
                          'getTotalReceivementDailySummaryByPersonId'
                        ),
                      });
                    },
                    error: (error: HttpErrorResponse) => {
                      patchState(store, {
                        receivementSummary: null,
                        ...setPropError(
                          'getTotalReceivementDailySummaryByPersonId',
                          error
                        ),
                      });
                    },
                  })
                )
            )
          )
        ),

        getTotalAccountsReceivablesSummaryByPersonId: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, {
                ...setPropLoading(
                  'getTotalAccountsReceivablesSummaryByPersonId'
                ),
              })
            ),
            switchMap((personId) =>
              profileService
                .getTotalAccountsReceivablesSummaryByPersonId(personId)
                .pipe(
                  tapResponse({
                    next: (response) => {
                      patchState(store, {
                        accountsReceivablesSummary: response,
                        ...setPropLoaded(
                          'getTotalAccountsReceivablesSummaryByPersonId'
                        ),
                      });
                    },
                    error: (error: HttpErrorResponse) => {
                      patchState(store, {
                        accountsReceivablesSummary: null,
                        ...setPropError(
                          'getTotalAccountsReceivablesSummaryByPersonId',
                          error
                        ),
                      });
                    },
                  })
                )
            )
          )
        ),

        getTotalOpenMonthByPersonId: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, {
                ...setPropLoading('getTotalOpenMonthByPersonId'),
              })
            ),
            switchMap((personId) =>
              profileService.getTotalOpenMonthByPersonId(personId).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(store, {
                      openMonthByPerson: response,
                      ...setPropLoaded('getTotalOpenMonthByPersonId'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(store, {
                      openMonthByPerson: null,
                      ...setPropError('getTotalOpenMonthByPersonId', error),
                    });
                  },
                })
              )
            )
          )
        ),

        getTimelineByPeriod: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('getTimelineByPeriod') })
            ),
            switchMap((yearMonth) =>
              profileService
                .getTimelineByPeriod(store.personId(), yearMonth)
                .pipe(
                  tapResponse({
                    next: ({ List, NextDate }) => {
                      const eventOnMillis = (eventOn: string): number => {
                        const parsed = getDateTime(eventOn);
                        return parsed?.isValid ? parsed.toMillis() : 0;
                      };
                      const orderedList = List.sort((a, b) =>
                        eventOnMillis(a.EventOn) < eventOnMillis(b.EventOn)
                          ? 1
                          : -1
                      );
                      const TimelineEvents = [
                        ...store.TimelineEvents(),
                        ...orderedList,
                      ];
                      patchState(store, {
                        TimelineNextDate: NextDate,
                        TimelineEvents,
                        ...setPropLoaded('getTimelineByPeriod'),
                      });
                    },
                    error: (error: HttpErrorResponse) => {
                      errorStore.setHttpError(error);
                      patchState(store, {
                        ...setPropError('getTimelineByPeriod', error),
                      });
                    },
                  })
                )
            )
          )
        ),

        toggleGraduationModal: (
          showGraduationModal = !store.showGraduationModal(),
          graduation: Partial<TGraduation> = null
        ) => {
          patchState(store, { showGraduationModal, graduation });
        },

        getAllGraduationsByPersonId: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, {
                ...setPropLoading('getAllGraduationsByPersonId'),
              })
            ),
            switchMap((personId) =>
              profileService.getAllGraduationsByPersonId(personId).pipe(
                tapResponse({
                  next: (graduations) => {
                    const graduationMillis = (graduationOn: string): number => {
                      const parsed = getDateTime(graduationOn);
                      return parsed?.isValid ? parsed.toMillis() : 0;
                    };
                    graduations.sort(
                      (a, b) =>
                        graduationMillis(b.GraduationOn) -
                        graduationMillis(a.GraduationOn)
                    );
                    for (const graduation of graduations) {
                      graduation?.Level?.SubLevels?.sort(
                        (a, b) => a.SubLevelOrder - b.SubLevelOrder
                      );
                    }
                    patchState(
                      store,
                      { graduations },
                      setPropLoaded('getAllGraduationsByPersonId')
                    );
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, {
                      ...setPropError('getAllGraduationsByPersonId', error),
                    });
                  },
                })
              )
            )
          )
        ),

        deleteGraduationById,

        openDialogDeleteGraduation: (
          graduationId: string,
          deleteGraduationLoading: Signal<boolean>,
          deleteGraduationLoaded: Signal<boolean>,
          deleteGraduationError: Signal<boolean>
        ): void => {
          dialogStore.openDialog({
            title: 'PROFILE.GRADUATION.DIALOG.TITLE',
            message: 'PROFILE.GRADUATION.DIALOG.MESSAGE',
            closeOnSignal: [deleteGraduationLoaded, deleteGraduationError],
            secondaryButton: {
              label: 'COMPONENTS.BUTTON.YES_DELETE',
              type: 'destructive',
              loadingOnsignal: deleteGraduationLoading,
              action: () => {
                deleteGraduationById(graduationId);
              },
            },
            primaryButton: {
              label: 'COMPONENTS.BUTTON.CANCEL',
              action: () => {
                dialogStore.closeDialog();
              },
            },
          });
        },

        createGraduation: rxMethod<Omit<TGraduation, 'PersonActivityLevelId'>>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('createGraduation') })
            ),
            switchMap((payload) =>
              profileService.createGraduation(payload).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(store, {
                      graduation: response,
                      ...setPropLoaded('createGraduation'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, {
                      ...setPropError('createGraduation', error),
                    });
                  },
                })
              )
            )
          )
        ),

        updateGraduation: rxMethod<TGraduation>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('updateGraduation') })
            ),
            switchMap((payload) =>
              profileService.updateGraduation(payload).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(store, {
                      graduation: response,
                      ...setPropLoaded('updateGraduation'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, {
                      ...setPropError('updateGraduation', error),
                    });
                  },
                })
              )
            )
          )
        ),

        getContractInfoByPersonId: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, {
                ...setPropLoading('getContractInfoByPersonId'),
              })
            ),
            switchMap((personId) =>
              profileService.getContractInfoByPersonId(personId).pipe(
                tapResponse({
                  next: (contractInfo) => {
                    patchState(store, {
                      contractInfo,
                      ...setPropLoaded('getContractInfoByPersonId'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, {
                      ...setPropError('getContractInfoByPersonId', error),
                    });
                  },
                })
              )
            )
          )
        ),

        copyToPaguela: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('copyToPaguela') })
            ),
            switchMap((id) =>
              profileService.copyToPaguela(id).pipe(
                tapResponse({
                  next: (paguelaProfile) => {
                    const profile = structuredClone(store.profile());
                    profile.Integration = {
                      ...(profile.Integration || {}),
                      PagueLaId: paguelaProfile?.PersonId,
                    };
                    patchState(store, {
                      profile,
                      ...setPropLoaded('copyToPaguela'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, {
                      ...setPropError('copyToPaguela', error),
                    });
                  },
                })
              )
            )
          )
        ),

        generateSimpleLoginCode: rxMethod<string>(
          pipe(
            filter(
              (personId): personId is string =>
                personId != null && personId !== ''
            ),
            tap(() =>
              patchState(store, {
                ...setPropLoading('generateSimpleLoginCode'),
              })
            ),
            switchMap((personId) =>
              profileService.generateSimpleLoginCode(personId).pipe(
                tapResponse({
                  next: (simpleLoginCode) => {
                    patchState(store, {
                      simpleLoginCode,
                      ...setPropLoaded('generateSimpleLoginCode'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, {
                      ...setPropError('generateSimpleLoginCode', error),
                    });
                  },
                })
              )
            )
          )
        ),

        simulateAccess: rxMethod<string>(
          pipe(
            filter(
              (personId): personId is string =>
                personId != null && personId !== ''
            ),
            tap(() =>
              patchState(store, {
                ...setPropLoading('simulateAccess'),
                simulateAccessMessage: null,
              })
            ),
            switchMap((personId) =>
              accessPointEdgeService.simulateAccess(personId).pipe(
                tapResponse({
                  next: (res) => {
                    const allowed = res?.IsAllowed === true;
                    patchState(store, {
                      simulateAccessAllowed: allowed,
                      simulateAccessMessage: res?.Message ?? null,
                      lastSimulatedAt: DateTime.now().toISO(),
                      ...setPropLoaded('simulateAccess'),
                    });
                  },
                  error: () => {
                    patchState(store, {
                      lastSimulatedAt: DateTime.now().toISO(),
                      ...setPropError('simulateAccess', null),
                    });
                  },
                })
              )
            )
          )
        ),

        getPersonCategoryHistoryByPersonId: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, {
                ...setPropLoading('getPersonCategoryHistoryByPersonId'),
              })
            ),
            switchMap((personId) =>
              profileService.getPersonCategoryHistoryByPersonId(personId).pipe(
                tapResponse({
                  next: (personCategoryHistory) => {
                    patchState(store, {
                      personCategoryHistory,
                      ...setPropLoaded('getPersonCategoryHistoryByPersonId'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, {
                      ...setPropError(
                        'getPersonCategoryHistoryByPersonId',
                        error
                      ),
                    });
                  },
                })
              )
            )
          )
        ),

        getPersonCategoryByPersonId: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, {
                ...setPropLoading('getPersonCategoryByPersonId'),
              })
            ),
            switchMap((personId) =>
              profileService.getPersonCategoryByPersonId(personId).pipe(
                tapResponse({
                  next: (personCategory) => {
                    patchState(store, {
                      personCategory,
                      ...setPropLoaded('getPersonCategoryByPersonId'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, {
                      ...setPropError('getPersonCategoryByPersonId', error),
                    });
                  },
                })
              )
            )
          )
        ),

        getAllCreditAssessmentPJ: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, {
                ...setPropLoading('getAllCreditAssessmentPJ'),
              })
            ),
            switchMap((companyFiscalIdentity) =>
              profileService
                .getAllCreditAssessmentPJ(companyFiscalIdentity)
                .pipe(
                  tapResponse({
                    next: (allCreditAssessmentPJ) => {
                      patchState(store, {
                        allCreditAssessmentPJ,
                        ...setPropLoaded('getAllCreditAssessmentPJ'),
                      });
                    },
                    error: (error: HttpErrorResponse) => {
                      patchState(store, {
                        ...setPropError('getAllCreditAssessmentPJ', error),
                      });
                    },
                  })
                )
            )
          )
        ),

        getSerasaPJById: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('getSerasaPJById') })
            ),
            switchMap((id) =>
              profileService.getSerasaPJById(id).pipe(
                tapResponse({
                  next: (creditAssessmentPJ) => {
                    patchState(store, {
                      creditAssessmentPJ,
                      ...setPropLoaded('getSerasaPJById'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(store, {
                      ...setPropError('getSerasaPJById', error),
                    });
                  },
                })
              )
            )
          )
        ),

        getCreditAssessmentPJ: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('getCreditAssessmentPJ') })
            ),
            switchMap((companyFiscalIdentity) =>
              profileService.getCreditAssessmentPJ(companyFiscalIdentity).pipe(
                tapResponse({
                  next: (creditAssessmentPJ) => {
                    patchState(store, {
                      creditAssessmentPJ,
                      ...setPropLoaded('getCreditAssessmentPJ'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(store, {
                      ...setPropError('getCreditAssessmentPJ', error),
                    });
                  },
                })
              )
            )
          )
        ),

        getLastCreditAssessmentPJ: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, {
                ...setPropLoading('getLastCreditAssessmentPJ'),
              })
            ),
            switchMap((companyFiscalIdentity) =>
              profileService
                .getLastCreditAssessmentPJ(companyFiscalIdentity)
                .pipe(
                  tapResponse({
                    next: (creditAssessmentPJ) => {
                      patchState(store, {
                        creditAssessmentPJ,
                        ...setPropLoaded('getLastCreditAssessmentPJ'),
                      });
                    },
                    error: (error: HttpErrorResponse) => {
                      patchState(store, {
                        ...setPropError('getLastCreditAssessmentPJ', error),
                      });
                    },
                  })
                )
            )
          )
        ),

        getAllCreditAssessmentPF: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, {
                ...setPropLoading('getAllCreditAssessmentPF'),
              })
            ),
            switchMap((fiscalIdentity) =>
              profileService.getAllCreditAssessmentPF(fiscalIdentity).pipe(
                tapResponse({
                  next: (allCreditAssessmentPF) => {
                    patchState(store, {
                      allCreditAssessmentPF,
                      ...setPropLoaded('getAllCreditAssessmentPF'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(store, {
                      ...setPropError('getAllCreditAssessmentPF', error),
                    });
                  },
                })
              )
            )
          )
        ),

        getSerasaPFById: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('getSerasaPFById') })
            ),
            switchMap((id) =>
              profileService.getSerasaPFById(id).pipe(
                tapResponse({
                  next: (creditAssessmentPF) => {
                    patchState(store, {
                      creditAssessmentPF,
                      ...setPropLoaded('getSerasaPFById'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(store, {
                      ...setPropError('getSerasaPFById', error),
                    });
                  },
                })
              )
            )
          )
        ),

        getCreditAssessmentPF: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('getCreditAssessmentPF') })
            ),
            switchMap((fiscalIdentity) =>
              profileService.getCreditAssessmentPF(fiscalIdentity).pipe(
                tapResponse({
                  next: (creditAssessmentPF) => {
                    patchState(store, {
                      creditAssessmentPF,
                      ...setPropLoaded('getCreditAssessmentPF'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(store, {
                      ...setPropError('getCreditAssessmentPF', error),
                    });
                  },
                })
              )
            )
          )
        ),

        getLastCreditAssessmentPF: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, {
                ...setPropLoading('getLastCreditAssessmentPF'),
              })
            ),
            switchMap((fiscalIdentity) =>
              profileService.getLastCreditAssessmentPF(fiscalIdentity).pipe(
                tapResponse({
                  next: (creditAssessmentPF) => {
                    patchState(store, {
                      creditAssessmentPF,
                      ...setPropLoaded('getLastCreditAssessmentPF'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(store, {
                      ...setPropError('getLastCreditAssessmentPF', error),
                    });
                  },
                })
              )
            )
          )
        ),

        setPropsInit(props: string[]): void {
          patchState(store, setPropInit(...props));
        },

        blankAssessmentCredit: () => {
          patchState(
            store,
            {
              creditAssessmentPF: null,
              creditAssessmentPJ: null,
              allCreditAssessmentPF: null,
              allCreditAssessmentPJ: null,
            },
            setPropInit(
              'getAllCreditAssessmentPJ',
              'getSerasaPJById',
              'getCreditAssessmentPJ',
              'getLastCreditAssessmentPJ',
              'getAllCreditAssessmentPF',
              'getSerasaPFById',
              'getCreditAssessmentPF',
              'getLastCreditAssessmentPF'
            )
          );
        },

        getIntegration: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('getIntegration') })
            ),
            switchMap((id) =>
              profileService.getIntegration(id).pipe(
                tapResponse({
                  next: (integration) => {
                    patchState(store, {
                      integration,
                      ...setPropLoaded('getIntegration'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(store, {
                      ...setPropError('getIntegration', error),
                    });
                  },
                })
              )
            )
          )
        ),

        updateIntegration: rxMethod<TPersonIntegration>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('updateIntegration') })
            ),
            switchMap((data) => {
              if (!data.TotalpassId) return of(data);
              return profileService.checkTotalPassKey(data.TotalpassId).pipe(
                map(() => data),
                tapResponse({
                  next: () => void 0,
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, {
                      ...setPropError('updateIntegration', error),
                    });
                  },
                })
              );
            }),
            switchMap((data) =>
              profileService.updateIntegration(data).pipe(
                tapResponse({
                  next: (integration) => {
                    alertStore.create({
                      message: 'PROFILE.INTEGRATION_UPDATED_SUCCESSFULLY',
                      type: 'success',
                      title: 'COMPONENTS.ALERT.SUCCESS',
                    });
                    patchState(store, {
                      integration,
                      ...setPropLoaded('updateIntegration'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, {
                      ...setPropError('updateIntegration', error),
                    });
                  },
                })
              )
            )
          )
        ),

        getProfileProducts: rxMethod<string>(
          pipe(
            tap(() => patchState(store, setPropLoading('getProfileProducts'))),
            switchMap((personId) =>
              profileService.getPersonProducts(personId).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(
                      store,
                      { profileProducts: response },
                      setPropLoaded('getProfileProducts')
                    );
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, {
                      ...setPropError('getProfileProducts', error),
                    });
                  },
                })
              )
            )
          )
        ),

        resetTimeline: () => {
          patchState(store, {
            TimelineEvents: [],
            TimelineNextDate: null,
            ...setPropInit('getTimelineByPeriod'),
          });
        },

        getPaguelaAccount: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('getPaguelaAccount') })
            ),
            switchMap((id) =>
              profileService.getPaguelaAccount(id).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(store, {
                      paguelaAccount: response,
                      ...setPropLoaded('getPaguelaAccount'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, {
                      ...setPropError('getPaguelaAccount', error),
                    });
                  },
                })
              )
            )
          )
        ),

        updatePaguelaSituation: rxMethod<{
          id: string;
          situation: EPaguelaSituation;
        }>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('updatePaguelaSituation') })
            ),
            switchMap(({ id, situation }) =>
              profileService.updatePaguelaSituation(id, situation).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(store, {
                      paguelaAccount: response,
                      ...setPropLoaded('updatePaguelaSituation'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, {
                      ...setPropError('updatePaguelaSituation', error),
                    });
                  },
                })
              )
            )
          )
        ),

        getActivities: rxMethod<void>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('getActivities') })
            ),
            switchMap(() =>
              profileService.getAllActivities().pipe(
                tapResponse({
                  next: (response) => {
                    patchState(store, {
                      activities: response?.value,
                      ...setPropLoaded('getActivities'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, {
                      ...setPropError('getActivities', error),
                    });
                  },
                })
              )
            )
          )
        ),

        getActivitiesByPersonId: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, {
                ...setPropLoading('getActivitiesByPersonId'),
              })
            ),
            switchMap((id) =>
              profileService.getActivitiesByPersonId(id).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(store, {
                      profileActivities: response?.value,
                      ...setPropLoaded('getActivitiesByPersonId'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, {
                      ...setPropError('getActivitiesByPersonId', error),
                    });
                  },
                })
              )
            )
          )
        ),

        blankActivities: () => {
          patchState(store, {
            profileActivities: [],
            ...setPropInit('getActivitiesByPersonId'),
          });
        },

        loadTiePersonDisplayNames: rxMethod<string[]>(
          pipe(
            switchMap((personIds) => {
              const unique = [...new Set(personIds.filter(Boolean))];
              const current = store.tiePersonDisplayNames();
              const currentContacts = store.tiePersonContacts();
              const missing = unique.filter(
                (id) =>
                  current[id] === undefined || currentContacts[id] === undefined
              );
              if (!missing.length) {
                return EMPTY;
              }
              patchState(store, { tiePersonDisplayNamesLoading: true });
              return forkJoin(
                missing.map((id) =>
                  profileService
                    .getPersonWithSpecificIncludes(id, [
                      EPersonIncludeType.Phones,
                    ])
                    .pipe(
                      map((p) => ({
                        id,
                        label:
                          p.FullDisplayName?.trim() ||
                          p.DisplayName?.trim() ||
                          [p.FirstName, p.LastName]
                            .filter(Boolean)
                            .join(' ')
                            .trim() ||
                          p.FullName?.trim() ||
                          '',
                        contact: {
                          Email: p.Email?.trim() || null,
                          HasPhone: (p.Phones?.length ?? 0) > 0,
                          HasMobilePhone: hasMobilePhone(p.Phones),
                        } as TTiePersonContact,
                      })),
                      catchError(() => of({ id, label: '', contact: null }))
                    )
                )
              ).pipe(
                tap((rows) => {
                  const next = { ...store.tiePersonDisplayNames() };
                  const nextContacts = { ...store.tiePersonContacts() };
                  for (const r of rows) {
                    if (r.label) {
                      next[r.id] = r.label;
                    }
                    if (r.contact) {
                      nextContacts[r.id] = r.contact;
                    }
                  }
                  patchState(store, {
                    tiePersonDisplayNames: next,
                    tiePersonContacts: nextContacts,
                  });
                }),
                finalize(() => {
                  patchState(store, { tiePersonDisplayNamesLoading: false });
                })
              );
            })
          )
        ),

        loadPersonsLookupByIds: rxMethod<string[]>(
          pipe(
            tap(() =>
              patchState(store, {
                ...setPropLoading('loadPersonsLookupByIds'),
              })
            ),
            concatMap((personIds) => {
              const unique = [...new Set(personIds.filter(Boolean))];
              const current = store.personsLookupById();
              const missing = unique.filter((id) => current[id] === undefined);

              if (!missing.length) {
                patchState(store, {
                  ...setPropLoaded('loadPersonsLookupByIds'),
                });
                return EMPTY;
              }

              // PersonsList filters out the tenant owner, so we have to look the
              // owner up via Person/GetOwnerInfo when its PersonId is requested.
              const tenantId = authStore.getAuthData()?.TenantId;
              const ownerId =
                tenantId && missing.includes(tenantId) ? tenantId : null;
              const nonOwnerIds = ownerId
                ? missing.filter((id) => id !== ownerId)
                : missing;

              const others$ = nonOwnerIds.length
                ? profileService.getPersonsLookupByIds(nonOwnerIds).pipe(
                    map((res) => res?.value ?? []),
                    catchError(() => of<TPersonLookupSummary[]>([]))
                  )
                : of<TPersonLookupSummary[]>([]);

              const owner$ = ownerId
                ? personService.getOwnerInfo(ownerId).pipe(
                    map<unknown, TPersonLookupSummary | null>(
                      (res: {
                        FullDisplayName?: string | null;
                        Email?: string | null;
                      }) => {
                        // Owner endpoint só devolve `FullDisplayName`. Espelhamos em
                        // `FullName` para consumidores que usam `displayExpr` de
                        // Users (que devolve `FullName`) ou que fazem fallback
                        // `FullDisplayName ?? FullName`.
                        const displayName = res?.FullDisplayName ?? null;
                        return {
                          PersonId: ownerId,
                          FullDisplayName: displayName,
                          FullName: displayName,
                          Email: res?.Email ?? null,
                          Phones: null,
                          IsActive: null,
                        };
                      }
                    ),
                    catchError(() => of<TPersonLookupSummary | null>(null))
                  )
                : of<TPersonLookupSummary | null>(null);

              return forkJoin({ others: others$, owner: owner$ }).pipe(
                tapResponse({
                  next: ({ others, owner }) => {
                    const next = { ...store.personsLookupById() };

                    for (const person of others) {
                      if (person?.PersonId) next[person.PersonId] = person;
                    }
                    if (owner) next[owner.PersonId] = owner;

                    patchState(store, {
                      personsLookupById: next,
                      ...setPropLoaded('loadPersonsLookupByIds'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, {
                      ...setPropError('loadPersonsLookupByIds', error),
                    });
                  },
                })
              );
            })
          )
        ),

        blankLoginCode: () => {
          patchState(
            store,
            { simpleLoginCode: null },
            setPropInit('generateSimpleLoginCode')
          );
        },

        blankPaguelaState: () => {
          patchState(
            store,
            { paguelaAccount: null },
            setPropInit('getPaguelaAccount', 'updatePaguelaSituation')
          );
        },

        getGroupByMemberTenantId: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, {
                ...setPropLoading('getGroupByMemberTenantId'),
              })
            ),
            switchMap((memberTenantId) =>
              accountService.getGroupByMemberTenantId(memberTenantId).pipe(
                tapResponse({
                  next: (group) => {
                    patchState(store, {
                      group,
                      ...setPropLoaded('getGroupByMemberTenantId'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, {
                      ...setPropError('getGroupByMemberTenantId', error),
                    });
                  },
                })
              )
            )
          )
        ),
      };
    }
  ),
  withReqState(
    'getProfile',
    'updatePerson',
    'createPerson',
    'setupTenantOwnerSettings',
    'verifyEmail',
    'isEmailAlreadyUsed',
    'checkIsPossibleToSetTemporaryEmail',
    'checkAccessCdExists',
    'checkCpfExists',
    'checkFullNameExists',
    'checkCompany',
    'reVerifyEmail',
    'toggleRead',
    'addComment',
    'updateComment',
    'removeComment',
    'getLastComment',
    'getTotalReceivementDailySummaryByPersonId',
    'getTotalAccountsReceivablesSummaryByPersonId',
    'patchPerson',
    'getResponsible',
    'createResponsible',
    'updateResponsible',
    'patchResponsible',
    'deleteResponsible',
    'loadPhones',
    'loadPhone',
    'createPhone',
    'updatePhone',
    'patchPhone',
    'deletePhone',
    'loadAddresses',
    'loadAddress',
    'createAddress',
    'updateAddress',
    'patchAddress',
    'deleteAddress',
    'getAccessFrequency',
    'getTotalOpenMonthByPersonId',
    'getComment',
    'profileEdit',
    'getTimelineByPeriod',
    'deleteGraduationById',
    'getAllGraduationsByPersonId',
    'createGraduation',
    'updateGraduation',
    'getContractInfoByPersonId',
    'copyToPaguela',
    'generateSimpleLoginCode',
    'simulateAccess',
    'getPersonCategoryHistoryByPersonId',
    'getPersonCategoryByPersonId',
    'getAllCreditAssessmentPJ',
    'getSerasaPJById',
    'getCreditAssessmentPJ',
    'getLastCreditAssessmentPJ',
    'getAllCreditAssessmentPF',
    'getSerasaPFById',
    'getCreditAssessmentPF',
    'getLastCreditAssessmentPF',
    'getIntegration',
    'updateIntegration',
    'getProfileProducts',
    'getPaguelaAccount',
    'updatePaguelaSituation',
    'getActivitiesByPersonId',
    'getActivities',
    'getOwnerSettings',
    'updateAppVersionAsync',
    'updateUseNewAccessControl',
    'loadTiePersonDisplayNames',
    'loadPersonsLookupByIds',
    'convertContact',
    'getGroupByMemberTenantId',
    'getSchoolEnrollmentsByPerson'
  )
);
